import {
    getChatPayloadPreview,
    getVisibleChatText,
    parseChatContentCardPayload,
    parseChatStickerPayload,
    normalizeChatContentCard,
} from "./contentPayloads";

export const CHAT_DEBUG =
    String(process.env.REACT_APP_CHAT_DEBUG || "") === "1" ||
    process.env.NODE_ENV === "development";

export const CHAT_MAX_MESSAGE_LENGTH = 4000;
export const CHAT_ACK_TIMEOUT_MS = Math.max(
    800,
    Number(process.env.REACT_APP_CHAT_ACK_TIMEOUT_MS || 1800) || 1800
);
export const UNSAFE_URL_PROTOCOL_RE = /^(?:javascript|vbscript|file|data:text\/html)/i;

export function dbg(...args) {
    if (CHAT_DEBUG) console.log(...args);
}

export function uid() {
    return `${Date.now().toString(36)}_${Math.random().toString(16).slice(2)}`;
}

export function toDate(v) {
    if (!v) return null;
    const d = v instanceof Date ? v : new Date(v);
    return Number.isNaN(d.getTime()) ? null : d;
}

export function isNonEmpty(v) {
    return v != null && String(v).trim() !== "";
}

export function pickFirst(...vals) {
    for (const v of vals) if (isNonEmpty(v)) return v;
    return null;
}

export function tryParseJSON(v) {
    if (v == null) return v;
    if (typeof v !== "string") return v;
    const s = v.trim();
    if (!s) return v;
    try {
        return JSON.parse(s);
    } catch {
        return v;
    }
}

export function stripUnsafeChars(v) {
    const s = String(v ?? "");
    let out = "";
    for (let i = 0; i < s.length; i += 1) {
        const code = s.charCodeAt(i);
        const isAllowedControl = code === 9 || code === 10 || code === 13; // tab / LF / CR
        if (((code >= 0 && code <= 31) && !isAllowedControl) || code === 127) continue;
        out += s[i];
    }
    return out;
}

export function sanitizeText(v, maxLen = CHAT_MAX_MESSAGE_LENGTH) {
    if (v == null) return "";
    const raw = stripUnsafeChars(v);
    return raw.slice(0, maxLen);
}

export function isUnsafeUrl(v) {
    const s = String(v || "").trim();
    if (!s) return true;
    if (UNSAFE_URL_PROTOCOL_RE.test(s)) return true;
    return false;
}

/**
 * Делает абсолютный URL для /images/... (админка на другом домене)
 * Настрой:
 *   REACT_APP_MEDIA_BASE_URL=https://api.example.com
 */
export function resolveMediaUrl(url) {
    if (!url) return undefined;
    const s = stripUnsafeChars(url).trim();
    if (!s || isUnsafeUrl(s)) return undefined;

    if (/^https?:\/\//i.test(s)) return s;
    if (s.startsWith("data:") || s.startsWith("blob:")) return s;

    // Схемы кроме http(s) (mailto:, ftp:, chrome-extension:, etc.) не допускаем.
    if (/^[a-zA-Z][a-zA-Z\d+\-.]*:/.test(s)) return undefined;

    const base =
        process.env.REACT_APP_MEDIA_BASE_URL ||
        process.env.REACT_APP_API_BASE_URL ||
        process.env.REACT_APP_BACKEND_URL ||
        "";

    if (!base) return s;

    const b = String(base).replace(/\/+$/, "");
    const p = s.startsWith("/") ? s : `/${s}`;
    return `${b}${p}`;
}

export function hasAny(obj, keys) {
    return keys.some((k) => obj != null && obj[k] != null);
}

export function messageKeyOf(m) {
    return m?.id || m?.clientId || `${m?.dialogId || "d"}:${String(m?.createdAt || "")}`;
}

// ------------------------------
// ✅ Members / Presence helpers
// ------------------------------

export function pageVisible() {
    if (typeof document === "undefined") return true;
    // visibilityState надежнее, но hidden тоже ок
    if (typeof document.visibilityState === "string") return document.visibilityState === "visible";
    if (typeof document.hidden === "boolean") return !document.hidden;
    return true;
}

export function keepIfNull(nextVal, prevVal) {
    return nextVal == null ? prevVal : nextVal;
}

export const CHAT_ADMIN_ROLE_CODES = new Set(["OWNER", "HOST", "CREATOR", "ADMIN", "MODERATOR"]);
export const CHAT_HOST_ROLE_CODES = new Set(["OWNER", "HOST", "CREATOR"]);
export const CHAT_MEMBER_MANAGE_ROLE_CODES = new Set(["OWNER", "HOST", "CREATOR", "ADMIN", "MODERATOR"]);

export function normalizeRoleCode(value) {
    if (!isNonEmpty(value)) return undefined;
    return String(value).trim().toUpperCase();
}

export function normMember(raw) {
    if (!raw) return null;

    const id =
        raw.userId ??
        raw.user?.id ??
        raw.id ??
        raw.memberId ??
        raw.user_id ??
        raw.member_id ??
        null;

    if (id == null) return null;

    const name =
        raw.name ??
        raw.user?.name ??
        raw.fullName ??
        raw.user?.fullName ??
        raw.title ??
        raw.displayName ??
        raw.email ??
        raw.user_name ??
        raw.full_name ??
        undefined;

    const avatarUrlRaw =
        raw.avatarUrl ??
        raw.avatar ??
        raw.photoUrl ??
        raw.avatar_url ??
        raw.photo_url ??
        raw.user?.avatarUrl ??
        raw.user?.avatar_url ??
        raw.user?.avatar ??
        raw.user?.photoUrl ??
        undefined;

    const profileUrlRaw =
        raw.profileUrl ??
        raw.profile_url ??
        raw.publicProfileUrl ??
        raw.public_profile_url ??
        raw.user?.profileUrl ??
        raw.user?.profile_url ??
        undefined;

    const onlinePresent =
        raw.online != null ||
        raw.isOnline != null ||
        raw.is_online != null;

    const online = onlinePresent
        ? Boolean(raw.online ?? raw.isOnline ?? raw.is_online)
        : undefined;

    const lastSeenRaw =
        raw.lastSeenAt ??
        raw.lastSeen ??
        raw.last_seen_at ??
        raw.last_seen ??
        undefined;

    const lastSeenAt = lastSeenRaw != null ? (toDate(lastSeenRaw) || undefined) : undefined;
    const roleCodeRaw =
        raw.role ??
        raw.memberRole ??
        raw.chatRole ??
        raw.membershipRole ??
        raw.member_role ??
        raw.chat_role ??
        raw.membership_role ??
        null;
    const roleCode = normalizeRoleCode(roleCodeRaw);

    const userRole = normalizeRoleCode(
        raw.userRole ??
            raw.user_role ??
            raw.accountRole ??
            raw.account_role ??
            raw.user?.role ??
            raw.profileRole ??
            raw.profile_role ??
            null
    );

    const isPsychologistPresent =
        raw.isPsychologist != null ||
        raw.is_psychologist != null ||
        raw.psychologist != null ||
        raw.user?.isPsychologist != null ||
        raw.user?.is_psychologist != null;
    const isPsychologist = isPsychologistPresent
        ? Boolean(raw.isPsychologist ?? raw.is_psychologist ?? raw.psychologist ?? raw.user?.isPsychologist ?? raw.user?.is_psychologist)
        : userRole
            ? userRole === "PSYCHOLOGIST"
            : roleCode
                ? roleCode === "PSYCHOLOGIST"
                : undefined;

    const isHostPresent =
        raw.isHost != null ||
        raw.host != null ||
        raw.isOwner != null ||
        raw.owner != null ||
        raw.isCreator != null ||
        raw.creator != null;
    const isHost = isHostPresent
        ? Boolean(raw.isHost ?? raw.host ?? raw.isOwner ?? raw.owner ?? raw.isCreator ?? raw.creator)
        : roleCode
            ? CHAT_HOST_ROLE_CODES.has(roleCode)
            : undefined;

    const isAdminPresent = raw.isAdmin != null || raw.admin != null || raw.is_admin != null;
    const isAdmin = isAdminPresent
        ? Boolean(raw.isAdmin ?? raw.admin ?? raw.is_admin)
        : roleCode
            ? CHAT_ADMIN_ROLE_CODES.has(roleCode)
            : isHost === true
                ? true
                : undefined;

    const canWritePresent =
        raw.canWrite != null ||
        raw.can_write != null ||
        raw.canSend != null ||
        raw.can_send != null ||
        raw.sendEnabled != null ||
        raw.send_enabled != null;
    const canWrite = canWritePresent
        ? Boolean(raw.canWrite ?? raw.can_write ?? raw.canSend ?? raw.can_send ?? raw.sendEnabled ?? raw.send_enabled)
        : undefined;

    const writeMutedPresent =
        raw.writeMuted != null ||
        raw.write_muted != null ||
        raw.isWriteMuted != null ||
        raw.is_write_muted != null ||
        raw.mutedForWrite != null ||
        raw.muted_for_write != null ||
        raw.sendDisabled != null ||
        raw.send_disabled != null ||
        raw.writeDisabled != null ||
        raw.write_disabled != null;
    const writeMuted = writeMutedPresent
        ? Boolean(
            raw.writeMuted ??
            raw.write_muted ??
            raw.isWriteMuted ??
            raw.is_write_muted ??
            raw.mutedForWrite ??
            raw.muted_for_write ??
            raw.sendDisabled ??
            raw.send_disabled ??
            raw.writeDisabled ??
            raw.write_disabled
        )
        : undefined;

    const writeMutedUntilRaw =
        raw.writeMutedUntil ??
        raw.write_muted_until ??
        raw.mutedUntil ??
        raw.muted_until ??
        raw.sendMutedUntil ??
        raw.send_muted_until ??
        undefined;
    const writeMutedUntil = writeMutedUntilRaw != null ? (toDate(writeMutedUntilRaw) || undefined) : undefined;

    const writeMuteReason =
        raw.writeMuteReason ??
        raw.write_mute_reason ??
        raw.muteReason ??
        raw.mute_reason ??
        raw.restrictionReason ??
        raw.restriction_reason ??
        undefined;

    return {
        userId: Number(id),
        id: Number(id),
        name,
        avatarUrl: avatarUrlRaw ? resolveMediaUrl(avatarUrlRaw) : undefined,
        profileUrl: profileUrlRaw ? resolveMediaUrl(profileUrlRaw) : undefined,
        isOnline: online,
        online: online, // compat
        lastSeenAt,
        roleCode,
        userRole,
        isPsychologist,
        isHost,
        isAdmin,
        canWrite,
        writeMuted,
        writeMutedUntil,
        writeMuteReason,
    };
}

export function mergeMembers(prevMembers, nextMembers) {
    const prev = Array.isArray(prevMembers) ? prevMembers : [];
    const next = Array.isArray(nextMembers) ? nextMembers : [];

    const map = new Map(prev.map((m) => [String(m.userId ?? m.id), m]));

    for (const m of next) {
        const key = String(m.userId ?? m.id);
        const old = map.get(key);
        if (!old) {
            map.set(key, m);
            continue;
        }

        const merged = { ...old, ...m };
        if (!isNonEmpty(m?.name) && isNonEmpty(old?.name)) merged.name = old.name;
        if (!isNonEmpty(m?.avatarUrl) && isNonEmpty(old?.avatarUrl)) merged.avatarUrl = old.avatarUrl;

        if (typeof m?.isOnline !== "boolean" && typeof old?.isOnline === "boolean") merged.isOnline = old.isOnline;
        if (typeof m?.online !== "boolean" && typeof old?.online === "boolean") merged.online = old.online;

        if (!(m?.lastSeenAt instanceof Date) && old?.lastSeenAt instanceof Date) merged.lastSeenAt = old.lastSeenAt;
        if (typeof m?.canWrite !== "boolean" && typeof old?.canWrite === "boolean") merged.canWrite = old.canWrite;
        if (typeof m?.writeMuted !== "boolean" && typeof old?.writeMuted === "boolean") merged.writeMuted = old.writeMuted;
        if (!(m?.writeMutedUntil instanceof Date) && old?.writeMutedUntil instanceof Date) merged.writeMutedUntil = old.writeMutedUntil;
        if (!isNonEmpty(m?.writeMuteReason) && isNonEmpty(old?.writeMuteReason)) merged.writeMuteReason = old.writeMuteReason;

        map.set(key, merged);
    }

    return Array.from(map.values());
}

export function countOnline(members) {
    const arr = Array.isArray(members) ? members : [];
    return arr.reduce((acc, m) => acc + ((m?.isOnline === true || m?.online === true) ? 1 : 0), 0);
}

/**
 * ✅ normDialog: НЕ подставляем дефолты, если поле не пришло
 * + добавили type/members/membersCount/onlineCount
 */
export function normDialog(raw) {
    if (!raw) return null;

    const id = raw.id ?? raw.dialogId ?? raw.chatId ?? raw.dialog_id ?? raw.chat_id;
    if (id == null) return null;

    const titlePresent = hasAny(raw, [
        "title",
        "name",
        "displayName",
        "partnerName",
        "partner_name",
        "display_name",
    ]);
    const title = titlePresent
        ? pickFirst(raw.title, raw.name, raw.displayName, raw.partnerName, raw.partner_name, raw.display_name) ||
        undefined
        : undefined;

    const avatarPresent = hasAny(raw, [
        "avatarUrl",
        "avatar",
        "photoUrl",
        "partnerAvatarUrl",
        "avatar_url",
        "partner_avatar_url",
        "photo_url",
    ]);
    const avatarUrlRaw = avatarPresent
        ? pickFirst(
        raw.avatarUrl,
        raw.avatar,
        raw.photoUrl,
        raw.partnerAvatarUrl,
        raw.avatar_url,
        raw.partner_avatar_url,
        raw.photo_url
    ) || undefined
        : undefined;

    const lastMessagePresent =
        hasAny(raw, ["lastMessage", "lastText", "preview", "last_message", "last_text", "last_message_text"]) ||
        (raw.last && raw.last.text != null);
    const lastMessageRaw = lastMessagePresent
        ? pickFirst(
        raw.lastMessage,
        raw.lastText,
        raw.last?.text,
        raw.preview,
        raw.last_message,
        raw.last_text,
        raw.last_message_text
    ) || undefined
        : undefined;
    const lastMessage = lastMessageRaw ? getChatPayloadPreview(lastMessageRaw) || undefined : undefined;

    const lastMessageSenderPresent =
        hasAny(raw, [
            "lastMessageSender",
            "lastMessageAuthor",
            "lastSenderName",
            "lastSender",
            "last_message_sender",
            "last_message_author",
            "last_sender_name",
            "last_sender",
        ]) ||
        (raw.last && hasAny(raw.last, ["senderName", "authorName", "fromName", "sender_name", "author_name", "from_name"]));
    const lastMessageSender = lastMessageSenderPresent
        ? pickFirst(
              raw.lastMessageSender,
              raw.lastMessageAuthor,
              raw.lastSenderName,
              raw.lastSender,
              raw.last_message_sender,
              raw.last_message_author,
              raw.last_sender_name,
              raw.last_sender,
              raw.last?.senderName,
              raw.last?.authorName,
              raw.last?.fromName,
              raw.last?.sender_name,
              raw.last?.author_name,
              raw.last?.from_name
          ) || undefined
        : undefined;

    const updatedAtPresent = hasAny(raw, [
        "updatedAt",
        "lastMessageAt",
        "lastAt",
        "modifiedAt",
        "createdAt",
        "updated_at",
        "last_message_at",
        "last_at",
        "modified_at",
        "created_at",
    ]);
    const updatedAt = updatedAtPresent
        ? toDate(
        raw.updatedAt ??
        raw.lastMessageAt ??
        raw.lastAt ??
        raw.modifiedAt ??
        raw.createdAt ??
        raw.updated_at ??
        raw.last_message_at ??
        raw.last_at ??
        raw.modified_at ??
        raw.created_at
    ) || undefined
        : undefined;

    const unreadPresent = hasAny(raw, ["unreadCount", "unread", "unreadMessages", "unread_count", "unread_messages"]);
    const unreadCount = unreadPresent
        ? Number(raw.unreadCount ?? raw.unread ?? raw.unreadMessages ?? raw.unread_count ?? raw.unread_messages) || 0
        : undefined;

    const onlinePresent =
        raw.online != null ||
        raw.isOnline != null ||
        raw.partnerIsOnline != null ||
        raw.partnerOnline != null ||
        raw.partner_is_online != null ||
        raw.partner_online != null;

    const online = onlinePresent
        ? Boolean(
            raw.online ??
            raw.isOnline ??
            raw.partnerIsOnline ??
            raw.partnerOnline ??
            raw.partner_is_online ??
            raw.partner_online
        )
        : undefined;

    const lastSeenPresent =
        raw.partnerLastSeenAt != null ||
        raw.lastSeenAt != null ||
        raw.partner_last_seen_at != null ||
        raw.last_seen_at != null;
    const lastSeenAtRaw = raw.partnerLastSeenAt ?? raw.lastSeenAt ?? raw.partner_last_seen_at ?? raw.last_seen_at;
    const lastSeenAt = lastSeenPresent ? toDate(lastSeenAtRaw) || undefined : undefined;

    const typingPresent = raw.typing != null || raw.isTyping != null || raw.is_typing != null;
    const typing = typingPresent ? Boolean(raw.typing ?? raw.isTyping ?? raw.is_typing) : undefined;

    const lockedPresent =
        raw.locked != null ||
        raw.isLocked != null ||
        raw.readOnly != null ||
        raw.locked_flag != null ||
        raw.read_only != null;
    const locked = lockedPresent
        ? Boolean(raw.locked ?? raw.isLocked ?? raw.readOnly ?? raw.locked_flag ?? raw.read_only)
        : undefined;

    // ✅ type / group
    const typePresent =
        raw.type != null ||
        raw.chatType != null ||
        raw.chat_type != null ||
        raw.dialogType != null ||
        raw.dialog_type != null ||
        raw.isGroup != null ||
        raw.is_group != null;

    const typeRaw = raw.type ?? raw.chatType ?? raw.chat_type ?? raw.dialogType ?? raw.dialog_type ?? null;
    const type = typePresent
        ? String(typeRaw ?? (raw.isGroup ?? raw.is_group ? "GROUP" : "DIRECT")).toUpperCase()
        : undefined;

    // ✅ members/meta (details endpoint)
    const membersPresent = Array.isArray(raw.members) || Array.isArray(raw.users) || Array.isArray(raw.participants);
    const membersRaw = raw.members ?? raw.users ?? raw.participants ?? null;
    const members = membersPresent ? (membersRaw || []).map(normMember).filter(Boolean) : undefined;

    const membersCountPresent = raw.membersCount != null || raw.members_count != null;
    const membersCount = membersCountPresent
        ? Number(raw.membersCount ?? raw.members_count) || 0
        : undefined;

    const onlineCountPresent = raw.onlineCount != null || raw.online_count != null;
    const onlineCount = onlineCountPresent
        ? Number(raw.onlineCount ?? raw.online_count) || 0
        : undefined;

    const partnerRole = normalizeRoleCode(
        raw.partnerRole ??
            raw.partner_role ??
            raw.otherUserRole ??
            raw.other_user_role ??
            raw.peerRole ??
            raw.peer_role ??
            raw.partner?.role ??
            null
    );

    const partnerProfileUrlRaw =
        raw.partnerProfileUrl ??
        raw.partner_profile_url ??
        raw.partnerUrl ??
        raw.partner_url ??
        raw.otherUserProfileUrl ??
        raw.other_user_profile_url ??
        raw.partner?.profileUrl ??
        raw.partner?.profile_url ??
        null;

    const psychologistUserIdRaw =
        raw.psychologistUserId ??
        raw.psychologist_user_id ??
        raw.psyUserId ??
        raw.psy_user_id ??
        raw.counsellorUserId ??
        raw.counsellor_user_id ??
        raw.therapistUserId ??
        raw.therapist_user_id ??
        raw.psychologist?.id ??
        raw.psychologist?.userId ??
        null;
    const psychologistUserId = psychologistUserIdRaw == null ? undefined : Number(psychologistUserIdRaw);

    const psychologistProfileUrlRaw =
        raw.psychologistProfileUrl ??
        raw.psychologist_profile_url ??
        raw.psychologist?.profileUrl ??
        raw.psychologist?.profile_url ??
        null;

    const hostUserIdRaw =
        raw.hostUserId ??
        raw.host_user_id ??
        raw.ownerUserId ??
        raw.owner_user_id ??
        raw.creatorUserId ??
        raw.creator_user_id ??
        raw.host?.id ??
        raw.owner?.id ??
        raw.creator?.id ??
        null;
    const hostUserId = hostUserIdRaw == null ? undefined : Number(hostUserIdRaw);

    const myRole = normalizeRoleCode(
        raw.myRole ??
            raw.my_role ??
            raw.myChatRole ??
            raw.my_chat_role ??
            raw.myMemberRole ??
            raw.my_member_role ??
            raw.currentUserRole ??
            raw.current_user_role ??
            raw.currentMemberRole ??
            raw.current_member_role ??
            null
    );

    const isAdminPresent =
        raw.isAdmin != null ||
        raw.is_admin != null ||
        raw.currentUserIsAdmin != null ||
        raw.current_user_is_admin != null ||
        raw.myIsAdmin != null ||
        raw.my_is_admin != null;

    const isAdmin = isAdminPresent
        ? Boolean(
              raw.isAdmin ??
                  raw.is_admin ??
                  raw.currentUserIsAdmin ??
                  raw.current_user_is_admin ??
                  raw.myIsAdmin ??
                  raw.my_is_admin
          )
        : myRole
            ? CHAT_ADMIN_ROLE_CODES.has(myRole)
            : undefined;

    const isHostPresent =
        raw.isHost != null ||
        raw.is_host != null ||
        raw.currentUserIsHost != null ||
        raw.current_user_is_host != null ||
        raw.myIsHost != null ||
        raw.my_is_host != null;

    const isHost = isHostPresent
        ? Boolean(
              raw.isHost ??
                  raw.is_host ??
                  raw.currentUserIsHost ??
                  raw.current_user_is_host ??
                  raw.myIsHost ??
                  raw.my_is_host
          )
        : myRole
            ? CHAT_HOST_ROLE_CODES.has(myRole)
            : undefined;

    const canManageMembersPresent =
        raw.canManageMembers != null ||
        raw.can_manage_members != null ||
        raw.manageMembers != null ||
        raw.manage_members != null ||
        raw.canInviteMembers != null ||
        raw.can_invite_members != null ||
        raw.canRemoveMembers != null ||
        raw.can_remove_members != null;

    const canManageMembers = canManageMembersPresent
        ? Boolean(
              raw.canManageMembers ??
                  raw.can_manage_members ??
                  raw.manageMembers ??
                  raw.manage_members ??
                  raw.canInviteMembers ??
                  raw.can_invite_members ??
                  raw.canRemoveMembers ??
                  raw.can_remove_members
          )
        : myRole
            ? CHAT_MEMBER_MANAGE_ROLE_CODES.has(myRole)
            : isAdmin === true || isHost === true
                ? true
                : undefined;

    const canWritePresent =
        raw.canWrite != null ||
        raw.can_write != null ||
        raw.canSend != null ||
        raw.can_send != null ||
        raw.sendEnabled != null ||
        raw.send_enabled != null;
    const canWrite = canWritePresent
        ? Boolean(raw.canWrite ?? raw.can_write ?? raw.canSend ?? raw.can_send ?? raw.sendEnabled ?? raw.send_enabled)
        : undefined;

    const writeMutedPresent =
        raw.writeMuted != null ||
        raw.write_muted != null ||
        raw.isWriteMuted != null ||
        raw.is_write_muted != null ||
        raw.mutedForWrite != null ||
        raw.muted_for_write != null ||
        raw.sendDisabled != null ||
        raw.send_disabled != null ||
        raw.writeDisabled != null ||
        raw.write_disabled != null ||
        raw.blockedFromSending != null ||
        raw.blocked_from_sending != null;
    const writeMuted = writeMutedPresent
        ? Boolean(
            raw.writeMuted ??
            raw.write_muted ??
            raw.isWriteMuted ??
            raw.is_write_muted ??
            raw.mutedForWrite ??
            raw.muted_for_write ??
            raw.sendDisabled ??
            raw.send_disabled ??
            raw.writeDisabled ??
            raw.write_disabled ??
            raw.blockedFromSending ??
            raw.blocked_from_sending
        )
        : undefined;

    const writeMutedUntilRaw =
        raw.writeMutedUntil ??
        raw.write_muted_until ??
        raw.mutedUntil ??
        raw.muted_until ??
        raw.sendMutedUntil ??
        raw.send_muted_until ??
        raw.blockedUntil ??
        raw.blocked_until ??
        null;
    const writeMutedUntil = writeMutedUntilRaw != null ? (toDate(writeMutedUntilRaw) || undefined) : undefined;

    const writeMuteReason =
        raw.writeMuteReason ??
        raw.write_mute_reason ??
        raw.muteReason ??
        raw.mute_reason ??
        raw.restrictionReason ??
        raw.restriction_reason ??
        undefined;

    return {
        id: String(id),
        title,
        avatarUrl: avatarUrlRaw ? resolveMediaUrl(avatarUrlRaw) : undefined,
        lastMessage,
        lastMessageSender,
        updatedAt,
        unreadCount,
        online,
        lastSeenAt,
        locked,
        typing,

        // ✅ new
        type,
        members,
        membersCount,
        onlineCount,
        partnerRole,
        partnerProfileUrl: partnerProfileUrlRaw ? resolveMediaUrl(partnerProfileUrlRaw) : undefined,
        psychologistUserId: Number.isFinite(psychologistUserId) ? psychologistUserId : undefined,
        psychologistProfileUrl: psychologistProfileUrlRaw ? resolveMediaUrl(psychologistProfileUrlRaw) : undefined,
        hostUserId: Number.isFinite(hostUserId) ? hostUserId : undefined,
        myRole,
        isAdmin,
        isHost,
        canManageMembers,
        canWrite,
        writeMuted,
        writeMutedUntil,
        writeMuteReason,
        settings:
            raw.settings ??
            raw.permissions ??
            raw.chatSettings ??
            raw.chat_settings ??
            undefined,
        permissions:
            raw.permissions ??
            raw.settings ??
            raw.chatPermissions ??
            raw.chat_permissions ??
            undefined,
        pinnedMessage:
            raw.pinnedMessage ??
            raw.pinned_message ??
            raw.pinned ??
            undefined,
        pinnedMessages:
            raw.pinnedMessages ??
            raw.pinned_messages ??
            undefined,

        partnerUserId:
            raw.partnerUserId ??
            raw.otherUserId ??
            raw.peerUserId ??
            raw.partner_user_id ??
            raw.other_user_id ??
            undefined,
    };
}

export function normAttachment(a) {
    if (!a) return null;
    const urlRaw = a.url ?? a.fileUrl ?? a.downloadUrl ?? a.file_url ?? a.download_url;
    const name = a.name ?? a.filename ?? a.originalName ?? a.original_name ?? "file";
    const size = a.size ?? a.bytes ?? null;
    const mime = a.mime ?? a.contentType ?? a.type ?? a.content_type ?? "";
    const kindRaw = String(a.kind ?? "").trim().toLowerCase();
    const normalizedName = String(name || "").split("#")[0].split("?")[0];
    const normalizedUrl = String(urlRaw || "").split("#")[0].split("?")[0];
    const looksLikeImageByExt = /\.(avif|bmp|gif|heic|heif|jpe?g|png|svg|webp)$/i.test(normalizedName) ||
        /\.(avif|bmp|gif|heic|heif|jpe?g|png|svg|webp)$/i.test(normalizedUrl);
    const kind =
        kindRaw ||
        (String(mime).startsWith("image/") || looksLikeImageByExt ? "image" : "file");
    const resolvedUrl = urlRaw ? resolveMediaUrl(urlRaw) : undefined;

    return {
        id: a.id ?? a.fileId ?? a.file_id ?? uid(),
        kind,
        url: resolvedUrl,
        name,
        size,
        mime,
    };
}

export function pickSenderId(m) {
    return (
        m.fromUserId ??
        m.senderUserId ??
        m.senderId ??
        m.authorUserId ??
        m.userId ??
        m.fromId ??
        m.from_user_id ??
        m.sender_user_id ??
        m.sender_id ??
        m.author_user_id ??
        m.sender?.id ??
        m.author?.id ??
        m.user?.id ??
        m.user_id ??
        m.from_id ??
        null
    );
}

export function normReplyTo(raw) {
    if (!raw) return null;
    const id = raw.id ?? raw.messageId ?? raw.message_id ?? null;
    if (id == null) return null;

    const mid = String(id);

    return {
        id: mid,
        messageId: mid,

        fromUserId:
            raw.fromUserId ??
            raw.senderUserId ??
            raw.userId ??
            raw.from_user_id ??
            raw.sender_user_id ??
            raw.user_id ??
            null,

        fromName: raw.fromName ?? raw.from_user_name ?? raw.senderName ?? raw.sender_name ?? null,
        text: raw.text ?? raw.content ?? raw.message ?? raw.body ?? "",
        createdAt: toDate(raw.createdAt ?? raw.created_when ?? raw.created_at) || null,
    };
}

export function normReactions(raw, meId) {
    if (!raw) return [];

    const arr = Array.isArray(raw) ? raw : Array.isArray(raw.items) ? raw.items : null;

    if (Array.isArray(arr)) {
        return arr
            .map((r) => {
                const emoji = r.emoji ?? r.e ?? r.reaction ?? null;
                if (!emoji) return null;

                let count = r.count ?? r.cnt ?? r.total ?? null;
                const usersRaw = r.users ?? r.reactedUsers ?? r.reacted_users ?? r.userList ?? r.user_list ?? r.userIds ?? r.user_ids ?? null;
                const users = Array.isArray(usersRaw)
                    ? usersRaw.map((user, index) => {
                        if (user == null) return null;
                        if (typeof user === "number" || typeof user === "string") {
                            return { userId: user, id: user, name: String(user) };
                        }
                        return {
                            ...user,
                            userId: user.userId ?? user.user_id ?? user.id ?? user.uid ?? `${emoji}-${index}`,
                            id: user.id ?? user.userId ?? user.user_id ?? user.uid ?? `${emoji}-${index}`,
                            name: user.name ?? user.fullName ?? user.full_name ?? user.displayName ?? user.display_name ?? user.username ?? `Участник ${index + 1}`,
                            avatarUrl: user.avatarUrl ?? user.avatar_url ?? user.avatar ?? "",
                        };
                    }).filter(Boolean)
                    : [];

                if (count == null && users.length) count = users.length;
                if (count == null) count = 1;

                let me = r.me ?? r.mine ?? r.isMine ?? r.is_mine;
                if (me == null && users.length && meId != null) {
                    me = users.some((x) => Number(x.userId ?? x.id) === Number(meId));
                }

                return { emoji: String(emoji), count: Number(count) || 0, me: Boolean(me), users };
            })
            .filter(Boolean)
            .filter((x) => x.count > 0);
    }

    if (typeof raw === "object") {
        const out = [];
        for (const [k, v] of Object.entries(raw)) {
            const count = Number(v) || 0;
            if (count > 0) out.push({ emoji: String(k), count, me: false });
        }
        return out;
    }

    return [];
}

export function normMessage(raw, dialogIdOverride, meId) {
    if (!raw) return null;

    const m = raw.message && typeof raw.message === "object" ? raw.message : raw;

    const dialogId =
        m.dialogId ??
        m.chatId ??
        m.threadId ??
        raw.dialogId ??
        dialogIdOverride ??
        m.dialog_id ??
        m.chat_id ??
        m.thread_id ??
        raw.dialog_id;

    if (dialogId == null) return null;

    const id = m.id ?? m.messageId ?? m.message_id ?? null;
    const mid = id == null ? null : String(id);

    const createdAt =
        toDate(
            m.createdWhen ??
            m.createdAt ??
            m.sentAt ??
            m.time ??
            m.ts ??
            m.created_when ??
            m.created_at ??
            m.sent_at ??
            m.ts_ms ??
            m.timestamp
        ) || new Date();

    const attachments = (m.attachments ?? m.files ?? m.attachments_list ?? m.file_list ?? [])
        .map(normAttachment)
        .filter(Boolean);

    const replyTo = normReplyTo(m.replyTo ?? m.reply_to ?? m.reply ?? null);

    const replyToMessageId =
        m.replyToMessageId ??
        m.reply_to_message_id ??
        m.replyToId ??
        m.reply_to_id ??
        replyTo?.messageId ??
        replyTo?.id ??
        null;

    const replyToClientId = m.replyToClientId ?? m.reply_to_client_id ?? null;
    const replyToKey = m.replyToKey ?? m.reply_to_key ?? replyToMessageId ?? replyToClientId ?? null;

    const rawText = sanitizeText(m.text ?? m.content ?? m.message ?? m.body ?? m.content_text ?? m.body_text ?? "");
    const eventTypeRaw =
        m.eventType ??
        m.event_type ??
        m.systemType ??
        m.system_type ??
        m.type ??
        null;
    const titleRaw =
        m.newTitle ??
        m.new_title ??
        m.chatTitle ??
        m.chat_title ??
        m.title ??
        null;
    const sticker = parseChatStickerPayload(rawText)
        || (m.stickerEmoji || m.sticker_emoji ? { emoji: sanitizeText(m.stickerEmoji ?? m.sticker_emoji, 16), type: "sticker" } : null);
    const contentCard = parseChatContentCardPayload(rawText)
        || (m.contentCard && typeof m.contentCard === "object" ? normalizeChatContentCard(m.contentCard) : null)
        || (m.content_card && typeof m.content_card === "object" ? normalizeChatContentCard(m.content_card) : null);
    const text = getVisibleChatText(rawText);
    const fromUserName =
        m.fromUserName ??
        m.from_name ??
        m.senderName ??
        m.sender_name ??
        m.authorName ??
        m.author_name ??
        m.sender?.name ??
        m.sender?.fullName ??
        m.author?.name ??
        m.author?.fullName ??
        m.user?.name ??
        m.user?.fullName ??
        null;
    const fromAvatarUrlRaw =
        m.fromAvatarUrl ??
        m.from_avatar_url ??
        m.senderAvatarUrl ??
        m.sender_avatar_url ??
        m.authorAvatarUrl ??
        m.author_avatar_url ??
        m.avatarUrl ??
        m.avatar_url ??
        m.sender?.avatarUrl ??
        m.sender?.avatar_url ??
        m.author?.avatarUrl ??
        m.author?.avatar_url ??
        m.user?.avatarUrl ??
        m.user?.avatar_url ??
        null;
    const senderIdRaw = pickSenderId(m);
    const fromUserId = senderIdRaw == null ? null : Number(senderIdRaw);

    const reactions = normReactions(m.reactions ?? m.reactionSummary ?? m.reaction_summary ?? null, meId);

    const deleted = Boolean(m.deleted ?? m.isDeleted ?? m.is_deleted ?? m.deleted_flag);

    const lastModifiedRaw =
        m.lastModified ??
        m.last_modified ??
        raw.lastModified ??
        raw.last_modified ??
        m.updatedAt ??
        m.updated_at ??
        null;

    const lastModifiedDt = toDate(lastModifiedRaw);

    const isEditedFlag = Boolean(m.isEdited ?? m.edited ?? m.wasEdited ?? m.is_edited);

    const editedAtDt =
        toDate(m.editedAt ?? m.edited_at ?? m.editedWhen ?? m.edited_when) || (isEditedFlag ? lastModifiedDt : null);

    const deletedWhenDt = toDate(m.deletedWhen ?? m.deleted_when ?? m.deletedAt ?? m.deleted_at) || null;

    const isReadByClient = m.isReadByClient ?? m.is_read_by_client ?? null;
    const isReadByPsychologist = m.isReadByPsychologist ?? m.is_read_by_psychologist ?? null;
    const readByMeRaw = m.readByMe ?? m.read_by_me ?? null;
    const readByOtherRaw = m.readByOther ?? m.read_by_other ?? null;
    const readAtRaw = m.readAt ?? m.read_at ?? null;
    const mine = meId != null && fromUserId != null && Number(fromUserId) === Number(meId);

    return {
        id: mid,
        clientId: m.clientId ?? raw.clientId ?? m.tempId ?? raw.tempId ?? m.client_id ?? raw.client_id ?? null,
        dialogId: String(dialogId),

        fromUserId,
        fromUserName,
        fromName: fromUserName,
        fromAvatarUrl: fromAvatarUrlRaw ? resolveMediaUrl(fromAvatarUrlRaw) : null,

        text,
        content: text,
        rawContent: rawText,
        sticker,
        contentCard,
        messageType: sticker
            ? "sticker"
            : contentCard
                ? "content_card"
                : (m.messageType ?? m.message_type ?? eventTypeRaw),
        eventType: eventTypeRaw,
        systemType: m.systemType ?? m.system_type ?? null,
        title: titleRaw,
        newTitle: m.newTitle ?? m.new_title ?? null,
        createdAt,

        status: m.status ?? (mid ? "sent" : "pending"),
        attachments,

        replyTo,
        replyToMessageId: replyToMessageId == null ? null : String(replyToMessageId),
        replyToClientId: replyToClientId == null ? null : String(replyToClientId),
        replyToKey: replyToKey == null ? null : String(replyToKey),

        reactions,

        // READ: НЕ делаем магии на фронте. Только то, что пришло, + локальные патчи markMessagesRead/WS.
        readAt: readAtRaw,
        isReadByClient: isReadByClient == null ? null : Boolean(isReadByClient),
        isReadByPsychologist: isReadByPsychologist == null ? null : Boolean(isReadByPsychologist),
        readByMe: readByMeRaw == null ? (mine ? true : null) : Boolean(readByMeRaw),
        readByOther: readByOtherRaw == null ? null : Boolean(readByOtherRaw),

        isEdited: isEditedFlag,
        editedAt: editedAtDt ? editedAtDt.toISOString() : m.editedAt ?? m.edited_at ?? null,
        lastModified: lastModifiedDt ? lastModifiedDt.toISOString() : lastModifiedRaw ?? null,

        deleted,
        isDeleted: deleted,
        deletedWhen: deletedWhenDt ? deletedWhenDt.toISOString() : m.deletedWhen ?? m.deleted_when ?? null,
        deletedAt: deletedWhenDt ? deletedWhenDt.toISOString() : m.deletedAt ?? m.deleted_at ?? null,

        messageId: mid,
    };
}

export function sortByUpdatedDesc(a, b) {
    const ta = a?.updatedAt instanceof Date ? a.updatedAt.getTime() : 0;
    const tb = b?.updatedAt instanceof Date ? b.updatedAt.getTime() : 0;
    return tb - ta;
}

export function extractItems(res) {
    if (!res) return [];
    if (Array.isArray(res)) return res;
    return res.items || res.content || res.messages || res.data?.items || res.data || [];
}

/**
 * REST typing=false не должен сбивать WS typing=true
 * + добавили merge для type/members/membersCount/onlineCount
 */
export function mergeDialogSafe(prev, next) {
    if (!prev) return next;
    if (!next) return prev;

    const merged = { ...prev, ...next };

    if (!isNonEmpty(next.title) && isNonEmpty(prev.title)) merged.title = prev.title;
    if (!isNonEmpty(next.avatarUrl) && isNonEmpty(prev.avatarUrl)) merged.avatarUrl = prev.avatarUrl;
    if (!isNonEmpty(next.lastMessage) && isNonEmpty(prev.lastMessage)) merged.lastMessage = prev.lastMessage;
    if (!isNonEmpty(next.lastMessageSender) && isNonEmpty(prev.lastMessageSender)) {
        merged.lastMessageSender = prev.lastMessageSender;
    }

    if (!(next.updatedAt instanceof Date) && prev.updatedAt instanceof Date) merged.updatedAt = prev.updatedAt;

    if (typeof next.typing !== "boolean" && typeof prev.typing === "boolean") merged.typing = prev.typing;
    if (typeof next.online !== "boolean" && typeof prev.online === "boolean") merged.online = prev.online;
    if (!(next.lastSeenAt instanceof Date) && prev.lastSeenAt instanceof Date) merged.lastSeenAt = prev.lastSeenAt;

    if (prev.typing === true && next.typing === false) merged.typing = true;

    if (typeof next.unreadCount !== "number" && typeof prev.unreadCount === "number") merged.unreadCount = prev.unreadCount;
    if (typeof next.locked !== "boolean" && typeof prev.locked === "boolean") merged.locked = prev.locked;

    // ✅ keep type from prev if not present in next
    if (typeof next.type !== "string" && typeof prev.type === "string") merged.type = prev.type;

    // ✅ members merge
    if (!Array.isArray(next.members) && Array.isArray(prev.members)) merged.members = prev.members;
    if (Array.isArray(next.members) && Array.isArray(prev.members)) merged.members = mergeMembers(prev.members, next.members);

    if (typeof next.membersCount !== "number" && typeof prev.membersCount === "number") merged.membersCount = prev.membersCount;
    if (typeof next.onlineCount !== "number" && typeof prev.onlineCount === "number") merged.onlineCount = prev.onlineCount;
    if (typeof next.partnerRole !== "string" && typeof prev.partnerRole === "string") merged.partnerRole = prev.partnerRole;
    if (!isNonEmpty(next.partnerProfileUrl) && isNonEmpty(prev.partnerProfileUrl)) merged.partnerProfileUrl = prev.partnerProfileUrl;
    if (next.psychologistUserId == null && prev.psychologistUserId != null) merged.psychologistUserId = prev.psychologistUserId;
    if (!isNonEmpty(next.psychologistProfileUrl) && isNonEmpty(prev.psychologistProfileUrl)) {
        merged.psychologistProfileUrl = prev.psychologistProfileUrl;
    }
    if (next.hostUserId == null && prev.hostUserId != null) merged.hostUserId = prev.hostUserId;
    if (typeof next.myRole !== "string" && typeof prev.myRole === "string") merged.myRole = prev.myRole;
    if (typeof next.isAdmin !== "boolean" && typeof prev.isAdmin === "boolean") merged.isAdmin = prev.isAdmin;
    if (typeof next.isHost !== "boolean" && typeof prev.isHost === "boolean") merged.isHost = prev.isHost;
    if (typeof next.canManageMembers !== "boolean" && typeof prev.canManageMembers === "boolean") {
        merged.canManageMembers = prev.canManageMembers;
    }
    if (typeof next.canWrite !== "boolean" && typeof prev.canWrite === "boolean") merged.canWrite = prev.canWrite;
    if (typeof next.writeMuted !== "boolean" && typeof prev.writeMuted === "boolean") merged.writeMuted = prev.writeMuted;
    if (!(next.writeMutedUntil instanceof Date) && prev.writeMutedUntil instanceof Date) {
        merged.writeMutedUntil = prev.writeMutedUntil;
    }
    if (!isNonEmpty(next.writeMuteReason) && isNonEmpty(prev.writeMuteReason)) merged.writeMuteReason = prev.writeMuteReason;

    return merged;
}

export function mergeMessages(prevArr, incomingArr) {
    const prev = Array.isArray(prevArr) ? prevArr : [];
    const inc = Array.isArray(incomingArr) ? incomingArr : [];

    const map = new Map();
    for (const m of prev) {
        const key = m?.id ? `id:${m.id}` : m?.clientId ? `c:${m.clientId}` : null;
        if (key) map.set(key, m);
    }

    for (const nm of inc) {
        const key = nm?.id ? `id:${nm.id}` : nm?.clientId ? `c:${nm.clientId}` : null;
        if (!key) continue;

        const old = map.get(key);
        if (!old) {
            map.set(key, nm);
            continue;
        }

        const merged = { ...old, ...nm };

        // ✅ НЕ затираем read-флаги/дату, если "новое" пришло null/undefined
        merged.readAt = keepIfNull(nm.readAt, old.readAt);
        merged.isReadByClient = keepIfNull(nm.isReadByClient, old.isReadByClient);
        merged.isReadByPsychologist = keepIfNull(nm.isReadByPsychologist, old.isReadByPsychologist);

        // если где-то UI смотрит на эти поля — тоже сохраняем
        merged.readByOther = keepIfNull(nm.readByOther, old.readByOther);
        merged.readByMe = keepIfNull(nm.readByMe, old.readByMe);

        const nmDeleted = Boolean(nm.deleted || nm.isDeleted || nm.deletedAt || nm.deletedWhen);

        if (!nmDeleted) {
            if ((!nm.attachments || nm.attachments.length === 0) && old.attachments?.length) merged.attachments = old.attachments;
            if (!isNonEmpty(nm.text) && isNonEmpty(old.text)) merged.text = old.text;
        } else {
            merged.text = "Сообщение удалено";
            merged.attachments = [];
            merged.reactions = [];
            merged.replyTo = null;
            merged.replyToKey = null;
            merged.replyToMessageId = null;
            merged.replyToClientId = null;
        }

        if (!nmDeleted) {
            if (!nm.replyTo && old.replyTo) merged.replyTo = old.replyTo;
            if (!nm.replyToKey && old.replyToKey) merged.replyToKey = old.replyToKey;
            if (!nm.replyToMessageId && old.replyToMessageId) merged.replyToMessageId = old.replyToMessageId;
            if (!nm.replyToClientId && old.replyToClientId) merged.replyToClientId = old.replyToClientId;

            if ((!nm.reactions || nm.reactions.length === 0) && old.reactions?.length) merged.reactions = old.reactions;
        }

        map.set(key, merged);
    }

    const out = Array.from(map.values());
    out.sort((a, b) => (toDate(a.createdAt)?.getTime() || 0) - (toDate(b.createdAt)?.getTime() || 0));
    return out;
}

// ------------------------------
// reactions helpers (optimistic)
// ------------------------------

export function upsertReactionSummary(current = [], emoji, nextMe, delta) {
    const arr = Array.isArray(current) ? [...current] : [];
    const ix = arr.findIndex((x) => String(x.emoji) === String(emoji));

    if (ix === -1) {
        const count = Math.max(0, Number(delta) || 0);
        if (count <= 0) return arr;
        arr.push({ emoji: String(emoji), count, me: Boolean(nextMe) });
        return arr;
    }

    const cur = arr[ix];
    const nextCount = Math.max(0, (Number(cur.count) || 0) + (Number(delta) || 0));
    if (nextCount <= 0) {
        arr.splice(ix, 1);
        return arr;
    }

    arr[ix] = { ...cur, count: nextCount, me: Boolean(nextMe) };
    return arr;
}

export function revokeLocalAttachments(message) {
    const attachments = Array.isArray(message?.attachments) ? message.attachments : [];
    for (const attachment of attachments) {
        const u = String(attachment?.url || "");
        if (!u.startsWith("blob:")) continue;
        if (!attachment?._local) continue;
        try {
            URL.revokeObjectURL(u);
        } catch {
            // ignore
        }
    }
}
